import { apiFetch } from './api';

/**
 * Override binaire d'une API sur l'env dev : faire tourner un build local à la place de
 * l'image publiée par le CI.
 *
 * Toute la mécanique vit côté backend (dépôt dans /home/container/override, hook recopié
 * sur /api par scripts/prerun.sh, bascule par renommage du dossier). Le panel ne fait que
 * lire l'état et déclencher les opérations : aucune convention de nommage de fichier n'est
 * connue ici, contrairement à l'ancienne version qui pilotait le file explorer à la main.
 */
export interface ApiOverrideFile {
  name: string;
  size: number;
  isFile: boolean;
}

export interface ApiOverrideState {
  apiId: string;
  environment: string;
  /** Clé d'instance, absente pour la Principale. */
  instance?: string;
  /** Un override est déposé (actif ou parké). */
  present: boolean;
  /** L'override est actif, donc appliqué au prochain démarrage. */
  active: boolean;
  /** Le hook est en place ET appelé par prerun.sh. Si false, l'override ne s'appliquera pas. */
  hookInstalled: boolean;
  files?: ApiOverrideFile[];
  /** Version du pipeline, celle qui tourne quand l'override est inactif. */
  gitVersion?: string;
  lastUploadName?: string;
  lastUploadSize?: number;
  lastUploadSha256?: string;
  lastUploadKind?: 'dll' | 'archive';
  lastAction?: string;
  lastActionAt?: string;
  lastActionBy?: string;
  lastActionByName?: string;
}

/** Réponse des mutations : déroulé serveur affichable tel quel. */
export interface ApiOverrideResult {
  ok: boolean;
  steps: string[];
  error?: string;
  state?: ApiOverrideState;
}

const base = (pid: string, apiId: string) => `/api/projects/${pid}/apis/${apiId}/override`;

/**
 * Query d'une opération : `instance` n'est ajouté que hors Principale, pour qu'un CICD plus
 * ancien reçoive exactement les mêmes appels qu'avant.
 */
function query(instance: string | undefined, restart?: boolean): string {
  const params = new URLSearchParams();
  if (restart !== undefined) params.set('restart', String(restart));
  if (instance && instance !== 'default') params.set('instance', instance);
  const q = params.toString();
  return q ? `?${q}` : '';
}

export const apiOverrideService = {
  state: (pid: string, apiId: string, instance?: string) =>
    apiFetch<ApiOverrideState>(`${base(pid, apiId)}${query(instance)}`),

  /** Dépose un .dll / .pdb / *.deps.json / archive publish, et active l'override. */
  deploy: (pid: string, apiId: string, file: File, restart = true, instance?: string) => {
    const form = new FormData();
    form.append('file', file);
    // Pas de Content-Type explicite : le navigateur doit poser le boundary multipart.
    return apiFetch<ApiOverrideResult>(`${base(pid, apiId)}${query(instance, restart)}`, {
      method: 'POST',
      body: form,
    });
  },

  /** Bascule override actif / version git. */
  toggle: (pid: string, apiId: string, enabled: boolean, restart = true, instance?: string) =>
    apiFetch<ApiOverrideResult>(`${base(pid, apiId)}/toggle${query(instance, restart)}`, {
      method: 'POST',
      json: { enabled },
    }),

  /** Supprime l'override : l'API repart sur l'image. */
  clear: (pid: string, apiId: string, restart = true, instance?: string) =>
    apiFetch<ApiOverrideResult>(`${base(pid, apiId)}${query(instance, restart)}`, { method: 'DELETE' }),

  /** Réinstalle le hook prerun (si un dev l'a retiré en éditant scripts/prerun.sh). */
  installHook: (pid: string, apiId: string, instance?: string) =>
    apiFetch<ApiOverrideResult>(`${base(pid, apiId)}/hook${query(instance)}`, { method: 'POST' }),
};
